// src/lib/v2Vocabulary.ts
//
// Human names for what the v2 read API states as bare words: breakdown keys
// (`identify`, `payment/abandoned_here`, `checkin/kiosk`, `card/rfid/initial`,
// `link_closed`...) and the field names inside a composed value (`count`,
// `amount_minor`, `completed_share`...). Every v2 screen resolves them here,
// so a word reads the same everywhere and an unknown one is shown in a
// readable form — never as the raw key.
import type { TranslateFunction } from 'react-admin';

const TERM_PREFIX = 'resources.insightV2.terms';
const FIELD_PREFIX = 'resources.insightV2.fields';
const ERROR_CODE_PREFIX = 'resources.insightV2.errorCodes';

/** Kiosk funnel order of the step kinds (01-domain-model.md §3.4). */
export const STEP_KIND_ORDER = [
  'identify',
  'reservation_confirm',
  'room_select',
  'party_capture',
  'document_capture',
  'document_verify',
  'signature',
  'terms',
  'upsell',
  'tourist_tax',
  'payment',
  'key_delivery',
  'print',
  'survey',
  'checkout_balance',
  'invoice',
  'summary',
];

/** `link_closed` → «Link closed»: the last resort for a word no list names. */
export const humanizeKey = (key: string): string => {
  const words = key.replace(/[_\-.]+/g, ' ').trim().toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
};

/** An error code of the contract's catalogue: `UPPER_SNAKE_CASE`, 3–64 characters, at least two words. */
export const isErrorCode = (key: string): boolean => /^[A-Z][A-Z0-9]*(_[A-Z0-9]+)+$/.test(key) && key.length <= 64;

/**
 * An error code by its name in the reader's language («Lectura del documento
 * fallida»); a code the catalogue does not know yet reads as a generic error
 * with the code as a reference for support, never as an English-looking phrase.
 */
export const errorCodeLabel = (code: string, translate: TranslateFunction): string =>
  translate(`${ERROR_CODE_PREFIX}.${code}`, { _: translate(`${ERROR_CODE_PREFIX}.unknown`, { code }) });

/** One word of the v2 vocabulary (a step kind, an outcome, a reason, an error code...). */
export const termLabel = (key: string, translate: TranslateFunction): string =>
  isErrorCode(key) ? errorCodeLabel(key, translate) : translate(`${TERM_PREFIX}.${key}`, { _: humanizeKey(key) });

const INCIDENT_CAUSE_PREFIX = 'resources.insightV2.incidentCauses';

/**
 * The cause of a device incident as the read API states it: `peripheral:<kind>:<id>`
 * («Fallo en el datáfono»), `pms_link_fault`, `device_blocked`... A cause no list
 * names reads as a generic incident, never as the raw key.
 */
export const incidentCauseLabel = (cause: string, translate: TranslateFunction): string => {
  const [head, kind] = cause.split(':');
  if (head === 'peripheral' && kind) {
    const component = translate(`${TERM_PREFIX}.${kind}`, { _: humanizeKey(kind) });
    return translate(`${INCIDENT_CAUSE_PREFIX}.peripheral`, { component });
  }
  return translate(`${INCIDENT_CAUSE_PREFIX}.${cause}`, { _: translate(`${INCIDENT_CAUSE_PREFIX}.unknown`) });
};

/** A composed breakdown key (`checkin/kiosk`), each part named: «Check-in · Kiosco». */
export const compositeTermLabel = (key: string, translate: TranslateFunction): string =>
  key
    .split('/')
    .map((part) => termLabel(part, translate))
    .join(' · ');

/** A field of a composed value (`amount_minor` → «Importe»). */
export const fieldLabel = (key: string, translate: TranslateFunction): string =>
  translate(`${FIELD_PREFIX}.${key}`, { _: humanizeKey(key) });

/** Position of a step kind in the funnel; unknown kinds go last, in their given order. */
export const stepKindRank = (kind: string): number => {
  const index = STEP_KIND_ORDER.indexOf(kind);
  return index === -1 ? STEP_KIND_ORDER.length : index;
};
